import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart3, Users, TrendingUp, Calendar, Trash2,
  ExternalLink, Eye, CheckCircle2, XCircle, Clock,
  ArrowLeft, ChevronDown, ChevronUp, Search,
  Sparkles, AlertTriangle, FileText, Euro, Building2, Pencil, LogOut
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const BACKEND_URL = "";
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(value);

const STATO_CONFIG = {
  in_attesa: { label: "In attesa", color: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
  accettato: { label: "Accettato", color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  rifiutato: { label: "Rifiutato", color: "bg-red-100 text-red-700 border-red-300", icon: XCircle },
};

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

export default function Dashboard() {
  const navigate = useNavigate();
  const [preventivi, setPreventivi] = useState([]);
  const [fatturato, setFatturato] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [annoView, setAnnoView] = useState("corrente");
  const [sortField, setSortField] = useState("ragione_sociale");
  const [sortDir, setSortDir] = useState("asc");
  const [expandedCliente, setExpandedCliente] = useState(null);

  const fetchData = async () => {
    try {
      const [prevRes, fattRes] = await Promise.all([
        fetch(`${API}/dashboard/riepilogo`),
        fetch(`${API}/dashboard/fatturato`),
      ]);
      if (prevRes.ok) setPreventivi(await prevRes.json());
      if (fattRes.ok) setFatturato(await fattRes.json());
    } catch {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API}/preventivi/${id}`, { method: "DELETE" });
      if (res.ok) {
        setPreventivi((prev) => prev.filter((p) => p.id !== id));
        setDeleteDialog(null);
        toast.success("Preventivo eliminato");
        // Refresh fatturato
        const fattRes = await fetch(`${API}/dashboard/fatturato`);
        if (fattRes.ok) setFatturato(await fattRes.json());
      }
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleStatoChange = async (id, newStato) => {
    try {
      const res = await fetch(`${API}/preventivi/${id}/stato`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stato: newStato }),
      });
      if (res.ok) {
        setPreventivi((prev) =>
          prev.map((p) => (p.id === id ? { ...p, stato_dashboard: newStato } : p))
        );
        toast.success(`Stato aggiornato: ${STATO_CONFIG[newStato].label}`);
        // Refresh fatturato
        const fattRes = await fetch(`${API}/dashboard/fatturato`);
        if (fattRes.ok) setFatturato(await fattRes.json());
      }
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleScadenza = async (id, date) => {
    try {
      const res = await fetch(`${API}/preventivi/${id}/scadenza`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data_scadenza: date || null }),
      });
      if (res.ok) {
        setPreventivi((prev) =>
          prev.map((p) => (p.id === id ? { ...p, data_scadenza: date || null } : p))
        );
        toast.success(date ? "Data scadenza impostata" : "Data scadenza rimossa");
        const fattRes = await fetch(`${API}/dashboard/fatturato`);
        if (fattRes.ok) setFatturato(await fattRes.json());
      }
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  // Sorted & filtered preventivi
  const filteredPreventivi = useMemo(() => {
    let list = [...preventivi];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          (p.cliente?.ragione_sociale || "").toLowerCase().includes(q) ||
          (p.numero_preventivo || "").toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      let valA, valB;
      if (sortField === "ragione_sociale") {
        valA = (a.cliente?.ragione_sociale || "").toLowerCase();
        valB = (b.cliente?.ragione_sociale || "").toLowerCase();
      } else if (sortField === "created_at") {
        valA = a.created_at || "";
        valB = b.created_at || "";
      } else if (sortField === "subtotale") {
        valA = a.subtotale || 0;
        valB = b.subtotale || 0;
      } else if (sortField === "visualizzazioni") {
        valA = a.visualizzazioni || 0;
        valB = b.visualizzazioni || 0;
      }
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [preventivi, search, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  // Stats
  const stats = useMemo(() => {
    const totali = preventivi.length;
    const accettati = preventivi.filter((p) => p.stato_dashboard === "accettato").length;
    const inAttesa = preventivi.filter((p) => !p.stato_dashboard || p.stato_dashboard === "in_attesa").length;
    const conUpgrade = preventivi.filter((p) => p.upgrade_richiesto).length;
    return { totali, accettati, inAttesa, conUpgrade };
  }, [preventivi]);

  // Raggruppamento per cliente
  const clientiData = useMemo(() => {
    const map = {};
    preventivi.forEach((p) => {
      const nome = p.cliente?.ragione_sociale || "Sconosciuto";
      const piva = p.cliente?.partita_iva || "";
      const key = piva || nome;
      if (!map[key]) {
        map[key] = { nome, piva, preventivi: [], totaleAccettato: 0, totaleInAttesa: 0, accettati: 0, inAttesa: 0, rifiutati: 0 };
      }
      map[key].preventivi.push(p);
      const scontoPct = p.sconto_percentuale || 0;
      const subM = p.subtotale_monthly || 0;
      const subO = p.subtotale_one_off || 0;
      const nettoM = subM - subM * (scontoPct / 100);
      const nettoO = subO - subO * (scontoPct / 100);
      const netto = nettoM + nettoO;
      const stato = p.stato_dashboard || "in_attesa";
      if (stato === "accettato") { map[key].totaleAccettato += netto; map[key].accettati++; }
      else if (stato === "rifiutato") { map[key].rifiutati++; }
      else { map[key].totaleInAttesa += netto; map[key].inAttesa++; }
    });
    return Object.values(map).sort((a, b) => b.totaleAccettato - a.totaleAccettato);
  }, [preventivi]);

  // Fatturato chart (simple bar)
  const maxFatturato = useMemo(() => {
    if (!fatturato) return 1;
    const key = annoView === "corrente" ? "fatturato_mensile_corrente" : "fatturato_mensile_successivo";
    const data = fatturato[key] || [];
    return Math.max(...data.map((d) => d.importo), 1);
  }, [fatturato, annoView]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Caricamento dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="btn-back-builder">
              <ArrowLeft className="w-4 h-4 mr-1" /> Builder
            </Button>
            <div className="h-5 w-px bg-slate-200" />
            <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" /> Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                placeholder="Cerca per ditta o numero..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 w-64 h-9 text-sm"
                data-testid="dashboard-search"
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { localStorage.removeItem("auth_token"); localStorage.removeItem("auth_nome"); window.location.href = "/"; }}
              data-testid="btn-logout"
              className="text-slate-500 hover:text-red-600 hover:bg-red-50"
              title="Esci"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-stats">
          <Card className="border-slate-200">
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Totale preventivi</p>
                <FileText className="w-4 h-4 text-slate-400" />
              </div>
              <p className="text-2xl font-bold text-slate-800">{stats.totali}</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-emerald-600 font-medium uppercase tracking-wide">Accettati</p>
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="text-2xl font-bold text-emerald-700">{stats.accettati}</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-amber-600 font-medium uppercase tracking-wide">In attesa</p>
                <Clock className="w-4 h-4 text-amber-500" />
              </div>
              <p className="text-2xl font-bold text-amber-700">{stats.inAttesa}</p>
            </CardContent>
          </Card>
          <Card className="border-violet-200 bg-violet-50/50">
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-violet-600 font-medium uppercase tracking-wide">Upgrade richiesti</p>
                <Sparkles className="w-4 h-4 text-violet-500" />
              </div>
              <p className="text-2xl font-bold text-violet-700">{stats.conUpgrade}</p>
            </CardContent>
          </Card>
        </div>

        {/* Report Fatturato */}
        {fatturato && (
          <Card className="border-slate-200" data-testid="fatturato-report">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-bold text-slate-800 flex items-center gap-2">
                  <Euro className="w-5 h-5 text-blue-600" /> Previsione Fatturato (netto)
                </CardTitle>
                <div className="flex gap-1">
                  <Button
                    variant={annoView === "corrente" ? "default" : "outline"}
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setAnnoView("corrente")}
                    data-testid="btn-anno-corrente"
                  >
                    {fatturato.anno_corrente}
                  </Button>
                  <Button
                    variant={annoView === "successivo" ? "default" : "outline"}
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setAnnoView("successivo")}
                    data-testid="btn-anno-successivo"
                  >
                    {fatturato.anno_successivo}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-1.5 h-40 mb-3" data-testid="fatturato-chart">
                {(annoView === "corrente"
                  ? fatturato.fatturato_mensile_corrente
                  : fatturato.fatturato_mensile_successivo
                ).map((d, i) => {
                  const height = maxFatturato > 0 ? (d.importo / maxFatturato) * 100 : 0;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] text-slate-500 font-medium">
                        {d.importo > 0 ? formatCurrency(d.importo) : ""}
                      </span>
                      <div
                        className="w-full rounded-t transition-all duration-300 bg-gradient-to-t from-blue-500 to-blue-400 min-h-[2px]"
                        style={{ height: `${Math.max(height, 2)}%` }}
                      />
                      <span className="text-[10px] text-slate-400">{d.mese}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-slate-100">
                <div>
                  <p className="text-xs text-slate-500">Totale annuo {annoView === "corrente" ? fatturato.anno_corrente : fatturato.anno_successivo}</p>
                  <p className="text-xl font-bold text-slate-800">
                    {formatCurrency(annoView === "corrente" ? fatturato.totale_annuo_corrente : fatturato.totale_annuo_successivo)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">Preventivi accettati</p>
                  <p className="text-xl font-bold text-emerald-600">{fatturato.preventivi_accettati}</p>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-2">
                Importi al netto di Cassa Previdenza (4%) e IVA (22%). Basato su preventivi con stato "Accettato".
              </p>
            </CardContent>
          </Card>
        )}

        {/* Tabella preventivi */}
        <Card className="border-slate-200" data-testid="preventivi-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-slate-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" /> Preventivi ({filteredPreventivi.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th
                      className="text-left px-4 py-2.5 font-semibold text-slate-600 cursor-pointer hover:text-slate-800 select-none"
                      onClick={() => toggleSort("ragione_sociale")}
                    >
                      <span className="flex items-center gap-1">Ditta <SortIcon field="ragione_sociale" /></span>
                    </th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600">N. Prev.</th>
                    <th
                      className="text-center px-3 py-2.5 font-semibold text-slate-600 cursor-pointer hover:text-slate-800 select-none"
                      onClick={() => toggleSort("subtotale")}
                    >
                      <span className="flex items-center justify-center gap-1">Netto annuo <SortIcon field="subtotale" /></span>
                    </th>
                    <th className="text-center px-3 py-2.5 font-semibold text-slate-600">Stato</th>
                    <th
                      className="text-center px-3 py-2.5 font-semibold text-slate-600 cursor-pointer hover:text-slate-800 select-none"
                      onClick={() => toggleSort("visualizzazioni")}
                    >
                      <span className="flex items-center justify-center gap-1"><Eye className="w-3 h-3" /> Visite <SortIcon field="visualizzazioni" /></span>
                    </th>
                    <th className="text-center px-3 py-2.5 font-semibold text-slate-600">Upgrade</th>
                    <th className="text-center px-3 py-2.5 font-semibold text-slate-600">Scadenza</th>
                    <th className="text-center px-3 py-2.5 font-semibold text-slate-600">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPreventivi.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-slate-400">
                        Nessun preventivo trovato
                      </td>
                    </tr>
                  )}
                  {filteredPreventivi.map((p) => {
                    const stato = p.stato_dashboard || "in_attesa";
                    const cfg = STATO_CONFIG[stato] || STATO_CONFIG.in_attesa;
                    const Icon = cfg.icon;
                    const nettoAnnuo = (p.subtotale || 0) - (p.sconto_valore || 0);
                    const upgradeNomi = {
                      controllo_strategico: "Strategico",
                      controllo_totale: "Totale",
                    };
                    return (
                      <tr
                        key={p.id}
                        className="border-b border-slate-100 hover:bg-blue-50/30 transition-colors"
                        data-testid={`preventivo-row-${p.id}`}
                      >
                        {/* Ditta */}
                        <td className="px-4 py-2.5">
                          <p className="font-semibold text-slate-800 text-sm">{p.cliente?.ragione_sociale || "—"}</p>
                          <p className="text-xs text-slate-400">{p.cliente?.partita_iva ? `P.IVA ${p.cliente.partita_iva}` : ""}</p>
                        </td>
                        {/* Numero */}
                        <td className="px-3 py-2.5">
                          <span className="text-xs font-mono text-slate-500">{p.numero_preventivo || "—"}</span>
                        </td>
                        {/* Netto annuo */}
                        <td className="text-center px-3 py-2.5">
                          <span className="font-semibold text-slate-800">{formatCurrency(nettoAnnuo)}</span>
                          {p.subtotale_monthly > 0 && (
                            <p className="text-[10px] text-slate-400">{formatCurrency((p.subtotale_monthly * 12 - (p.sconto_valore || 0)) / 12)}/mese</p>
                          )}
                        </td>
                        {/* Stato */}
                        <td className="text-center px-3 py-2.5">
                          <select
                            value={stato}
                            onChange={(e) => handleStatoChange(p.id, e.target.value)}
                            className={`text-xs font-medium px-2 py-1 rounded-full border cursor-pointer ${cfg.color}`}
                            data-testid={`stato-select-${p.id}`}
                          >
                            <option value="in_attesa">In attesa</option>
                            <option value="accettato">Accettato</option>
                            <option value="rifiutato">Rifiutato</option>
                          </select>
                        </td>
                        {/* Visualizzazioni */}
                        <td className="text-center px-3 py-2.5">
                          <div className="flex items-center justify-center gap-1">
                            <Eye className="w-3 h-3 text-slate-400" />
                            <span className="text-sm font-medium text-slate-600">{p.visualizzazioni || 0}</span>
                          </div>
                          {p.ultima_visualizzazione && (
                            <p className="text-[10px] text-slate-400">
                              {new Date(p.ultima_visualizzazione).toLocaleDateString("it-IT")}
                            </p>
                          )}
                        </td>
                        {/* Upgrade */}
                        <td className="text-center px-3 py-2.5">
                          {p.upgrade_richiesto ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full">
                              <Sparkles className="w-3 h-3" />
                              {upgradeNomi[p.upgrade_richiesto] || p.upgrade_richiesto}
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                        {/* Scadenza */}
                        <td className="text-center px-3 py-2.5">
                          <input
                            type="date"
                            value={p.data_scadenza ? p.data_scadenza.split("T")[0] : ""}
                            onChange={(e) => handleScadenza(p.id, e.target.value || null)}
                            className="text-xs border border-slate-200 rounded px-1.5 py-1 text-slate-600 w-28 bg-white hover:border-slate-400 transition-colors"
                            data-testid={`scadenza-input-${p.id}`}
                          />
                        </td>
                        {/* Azioni */}
                        <td className="text-center px-3 py-2.5">
                          <div className="flex items-center justify-center gap-1">
                            {stato !== "accettato" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-amber-500 hover:text-amber-700"
                                onClick={() => navigate(`/modifica/${p.id}`)}
                                title="Modifica preventivo"
                                data-testid={`btn-edit-${p.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {p.share_token && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-blue-500 hover:text-blue-700"
                                onClick={() => window.open(`/p/${p.share_token}`, "_blank")}
                                title="Apri presentazione"
                                data-testid={`btn-open-presentation-${p.id}`}
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                              onClick={() => setDeleteDialog(p)}
                              title="Elimina preventivo"
                              data-testid={`btn-delete-${p.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Fatturato per Cliente */}
        <Card className="border-slate-200" data-testid="fatturato-per-cliente">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-slate-800 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" /> Fatturato per Cliente ({clientiData.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left px-4 py-2.5 font-semibold text-slate-600 w-8"></th>
                    <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Cliente</th>
                    <th className="text-center px-3 py-2.5 font-semibold text-slate-600">Preventivi</th>
                    <th className="text-center px-3 py-2.5 font-semibold text-slate-600">Fatturato accettato</th>
                    <th className="text-center px-3 py-2.5 font-semibold text-slate-600">In attesa</th>
                    <th className="text-center px-3 py-2.5 font-semibold text-slate-600">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {clientiData.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-8 text-slate-400">Nessun cliente</td></tr>
                  )}
                  {clientiData.map((c) => {
                    const isExpanded = expandedCliente === c.piva || expandedCliente === c.nome;
                    const toggleKey = c.piva || c.nome;
                    return (
                      <React.Fragment key={toggleKey}>
                        <tr
                          className="border-b border-slate-100 hover:bg-blue-50/30 transition-colors cursor-pointer"
                          onClick={() => setExpandedCliente(isExpanded ? null : toggleKey)}
                          data-testid={`cliente-row-${toggleKey}`}
                        >
                          <td className="px-4 py-2.5 text-slate-400">
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </td>
                          <td className="px-4 py-2.5">
                            <p className="font-semibold text-slate-800">{c.nome}</p>
                            {c.piva && <p className="text-xs text-slate-400">P.IVA {c.piva}</p>}
                          </td>
                          <td className="text-center px-3 py-2.5">
                            <span className="font-bold text-slate-700">{c.preventivi.length}</span>
                          </td>
                          <td className="text-center px-3 py-2.5">
                            <span className="font-bold text-emerald-700">{formatCurrency(c.totaleAccettato)}</span>
                          </td>
                          <td className="text-center px-3 py-2.5">
                            {c.totaleInAttesa > 0 ? (
                              <span className="font-medium text-amber-600">{formatCurrency(c.totaleInAttesa)}</span>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="text-center px-3 py-2.5">
                            <div className="flex items-center justify-center gap-1.5">
                              {c.accettati > 0 && (
                                <span className="inline-flex items-center gap-0.5 text-xs font-medium text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">
                                  <CheckCircle2 className="w-3 h-3" /> {c.accettati}
                                </span>
                              )}
                              {c.inAttesa > 0 && (
                                <span className="inline-flex items-center gap-0.5 text-xs font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">
                                  <Clock className="w-3 h-3" /> {c.inAttesa}
                                </span>
                              )}
                              {c.rifiutati > 0 && (
                                <span className="inline-flex items-center gap-0.5 text-xs font-medium text-red-700 bg-red-100 px-1.5 py-0.5 rounded-full">
                                  <XCircle className="w-3 h-3" /> {c.rifiutati}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                        {/* Dettaglio preventivi del cliente */}
                        {isExpanded && c.preventivi.map((p) => {
                          const stato = p.stato_dashboard || "in_attesa";
                          const cfg = STATO_CONFIG[stato] || STATO_CONFIG.in_attesa;
                          const scontoPct = p.sconto_percentuale || 0;
                          const subM = p.subtotale_monthly || 0;
                          const subO = p.subtotale_one_off || 0;
                          const nettoM = subM - subM * (scontoPct / 100);
                          const nettoO = subO - subO * (scontoPct / 100);
                          return (
                            <tr key={p.id} className="bg-slate-50/80 border-b border-slate-100">
                              <td className="px-4 py-2"></td>
                              <td className="px-4 py-2">
                                <div className="flex items-center gap-2 pl-4">
                                  <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                  <div>
                                    <span className="text-xs font-mono text-slate-500">{p.numero_preventivo}</span>
                                    <p className="text-xs text-slate-400">
                                      {p.created_at ? new Date(p.created_at).toLocaleDateString("it-IT") : ""}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="text-center px-3 py-2">
                                <span className="text-xs text-slate-500">
                                  {p.servizi?.length || 0} servizi
                                </span>
                              </td>
                              <td className="text-center px-3 py-2">
                                <div>
                                  {nettoM > 0 && <p className="text-xs font-semibold text-slate-700">{formatCurrency(nettoM)}/anno</p>}
                                  {nettoO > 0 && <p className="text-xs text-slate-500">{formatCurrency(nettoO)} una tantum</p>}
                                </div>
                              </td>
                              <td className="text-center px-3 py-2">
                                {nettoM > 0 && <p className="text-xs text-slate-500">{formatCurrency(nettoM / 12)}/mese</p>}
                              </td>
                              <td className="text-center px-3 py-2">
                                <div className="flex items-center justify-center gap-2">
                                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${cfg.color}`}>
                                    {cfg.label}
                                  </span>
                                  {p.share_token && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700"
                                      onClick={(e) => { e.stopPropagation(); window.open(`/p/${p.share_token}`, "_blank"); }}
                                      title="Apri presentazione"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Conferma eliminazione
            </DialogTitle>
            <DialogDescription>
              Vuoi eliminare il preventivo di <strong>{deleteDialog?.cliente?.ragione_sociale}</strong>?
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>Annulla</Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteDialog?.id)}
              data-testid="btn-confirm-delete"
            >
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
